import { useState, useEffect } from 'react';
import { X, ArrowRight, Mic, FileText, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LensWorkflowDemo from '../shared/LensWorkflowDemo';

const LIGHTBOX_DISMISSED_KEY = 'lumina_workflow_lightbox_dismissed';
const INTEGRATION_KEY = 'lumina_integration_type';
const LUMINA_LOGO_BLACK = 'https://customer-assets.emergentagent.com/job_revenue-cycle-1/artifacts/rms1wglh_lumina_logo_black.png';

/* ── Inline SVG icons ── */
const SparkleIcon = ({ size = 11, color = '#888' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M8 0L9.8 6.2L16 8L9.8 9.8L8 16L6.2 9.8L0 8L6.2 6.2L8 0Z" fill={color} /></svg>
);

const LensMagnifier = ({ size = 13, color = '#555' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /><path d="M11 8v6" /><path d="M8 11h6" />
  </svg>
);

/* ── Step components ── */
const HumanStep = ({ num, title, badge, badgeStyle, desc, lensCta, onLensClick, label = 'You do this' }) => (
  <div style={{ background: '#fff', border: '0.5px solid #ebebeb', borderRadius: 9, padding: '10px 13px', display: 'flex', alignItems: 'flex-start', gap: 10 }} data-testid={`lumina-step-human-${num}`}>
    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#111', color: '#fff', fontSize: 10, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{num}</div>
    <div style={{ flex: 1 }}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span style={{ fontSize: 11, fontWeight: 500, color: '#111' }}>{title}</span>
        {badge && <span style={{ fontSize: 9, fontWeight: 500, padding: '1px 5px', borderRadius: 6, ...badgeStyle }}>{badge}</span>}
      </div>
      <p style={{ fontSize: 10, color: '#555', lineHeight: '1.5', marginTop: 3 }}>{desc}</p>
      {lensCta && (
        <button onClick={onLensClick} className="flex items-center gap-1" style={{ fontSize: 9, color: '#555', background: '#fff', border: '0.5px solid #e0e0e0', borderRadius: 5, padding: '2px 9px', height: 24, marginTop: 5, cursor: 'pointer' }} data-testid={`lumina-lens-cta-${num}`}>
          <LensMagnifier size={9} color="#555" />{lensCta}
        </button>
      )}
    </div>
    <span style={{ fontSize: 9, color: '#bbb', flexShrink: 0, whiteSpace: 'nowrap', marginTop: 2 }}>{label}</span>
  </div>
);

const AIStepLumina = ({ title, badge, badgeStyle, desc }) => (
  <div style={{ background: '#fafafa', border: '0.5px solid #f0f0f0', borderRadius: 9, padding: '10px 13px', display: 'flex', alignItems: 'flex-start', gap: 10 }} data-testid="lumina-step-ai">
    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
      <SparkleIcon size={11} color="#888" />
    </div>
    <div style={{ flex: 1 }}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span style={{ fontSize: 11, fontWeight: 500, color: '#111' }}>{title}</span>
        {badge && <span style={{ fontSize: 9, fontWeight: 500, padding: '1px 5px', borderRadius: 6, ...badgeStyle }}>{badge}</span>}
      </div>
      <p style={{ fontSize: 10, color: '#555', lineHeight: '1.5', marginTop: 3 }}>{desc}</p>
    </div>
    <span style={{ fontSize: 9, color: '#bbb', flexShrink: 0, whiteSpace: 'nowrap', marginTop: 2 }}>AI handles this</span>
  </div>
);

const Divider = ({ label }) => (
  <div className="flex items-center gap-3" style={{ margin: '4px 0' }}>
    <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
    <span style={{ fontSize: 9, color: '#ccc', whiteSpace: 'nowrap' }}>{label}</span>
    <div style={{ flex: 1, height: '0.5px', background: '#f0f0f0' }} />
  </div>
);

/* Badge style presets */
const BADGE = {
  fhir: { background: '#eff6ff', color: '#1e40af' },
  lens: { background: '#f9f9f9', color: '#555', border: '0.5px solid #e0e0e0' },
  ai: { background: '#f0f0f0', color: '#555' },
  clinical: { background: '#f0f0f0', color: '#555' },
  encounter: { background: '#f0f0f0', color: '#555' },
};

/* ── FHIR workflow (3 human steps) ── */
const FHIRWorkflow = ({ onLensClick }) => (
  <div className="flex flex-col gap-2">
    <AIStepLumina
      title="Today's patients load from your EHR"
      badge="FHIR" badgeStyle={BADGE.fhir}
      desc="Lumina pulls your schedule and patient charts automatically — demographics, insurance, prior visit notes, and problem lists are ready before you walk in the room."
    />
    <Divider label="You start the visit" />
    <HumanStep num={1}
      title="Start Clinical Focus and open the visit"
      badge="Clinical Focus" badgeStyle={BADGE.clinical}
      desc="Select a patient from your queue and begin. Tap 'Start ambient' to let Lumina listen and capture the conversation, or use dictation to narrate as you go. Lumina structures everything in real time."
    />
    <Divider label="Lumina AI writes the note" />
    <AIStepLumina
      title="AI generates a complete SOAP note"
      badge="AI" badgeStyle={BADGE.ai}
      desc="After the visit, Lumina produces a structured SOAP note — subjective, objective, assessment, and plan — pre-filled with diagnoses, medications, and billing codes ready for your review."
    />
    <HumanStep num={2}
      title="Review, sign, and export to your EHR"
      badge="FHIR" badgeStyle={BADGE.fhir}
      desc="Review the generated note, make any edits, and sign. Lumina pushes the completed note directly to your EHR via FHIR — no copy-paste, no switching systems."
    />
    <Divider label="Move to the next patient" />
    <HumanStep num={3}
      title="Scan clinical documents as needed"
      badge="Lens" badgeStyle={BADGE.lens}
      desc="For referral notes, lab results, or discharge summaries brought in by the patient — scan with Lumina Lens instead of re-entering data manually. Extracted content attaches to the visit automatically."
      lensCta="Try Lens demo" onLensClick={onLensClick} label="When needed"
    />
  </div>
);

/* ── Non-FHIR workflow (4 human steps) ── */
const NonFHIRWorkflow = ({ onLensClick }) => (
  <div className="flex flex-col gap-2">
    <HumanStep num={1}
      title="Scan today's schedule to load your patients"
      badge="Lens" badgeStyle={BADGE.lens}
      desc="Photograph your appointment list or patient roster from your EHR's printed schedule or screen. Lumina reads it and populates your patient queue in seconds — no manual entry."
      lensCta="Try scanning a schedule" onLensClick={onLensClick}
    />
    <HumanStep num={2}
      title="Scan clinical documents before each visit"
      badge="Lens" badgeStyle={BADGE.lens}
      desc="Referral letters, discharge summaries, lab results — scan them with Lens and the extracted content attaches to the patient record automatically. No re-entering data from prior providers."
      lensCta="Try scanning a document" onLensClick={onLensClick}
    />
    <Divider label="You start the visit" />
    <HumanStep num={3}
      title="Start Clinical Focus — ambient or dictate"
      badge="Clinical Focus" badgeStyle={BADGE.clinical}
      desc="Open the patient in Clinical Focus and begin. Tap ambient to let Lumina listen and structure the conversation, or dictate to record as you go. The note builds in real time."
    />
    <Divider label="Lumina AI writes the note" />
    <AIStepLumina
      title="AI generates a complete SOAP note"
      badge="AI" badgeStyle={BADGE.ai}
      desc="Lumina produces a structured SOAP note with diagnoses, medications, and billing codes — pre-filled from the visit conversation and scanned documents combined."
    />
    <HumanStep num={4}
      title="Review, sign, and export"
      badge="Encounter Review" badgeStyle={BADGE.encounter}
      desc="Review the note in Encounter Review, make any edits, sign, and export. Copy to your EHR, print for the patient, or send to billing — all from one screen."
    />
  </div>
);

/* ── Tools row ── */
const ToolsRow = ({ isNonFhir }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 14 }}>
    {/* Ambient Listening */}
    <div style={{ background: '#f9f9f9', border: '0.5px solid #e8e8e8', borderRadius: 8, padding: '9px 10px' }}>
      <div style={{ width: 24, height: 24, borderRadius: 6, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 }}>
        <Mic style={{ width: 13, height: 13, color: '#555' }} />
      </div>
      <p style={{ fontSize: 10, fontWeight: 500, color: '#111', marginBottom: 2 }}>Ambient listening</p>
      <p style={{ fontSize: 9, color: '#888', lineHeight: '1.3' }}>AI captures the visit conversation and structures it automatically</p>
    </div>
    {/* Dictation */}
    <div style={{ background: '#f9f9f9', border: '0.5px solid #e8e8e8', borderRadius: 8, padding: '9px 10px' }}>
      <div style={{ width: 24, height: 24, borderRadius: 6, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 }}>
        <FileText style={{ width: 13, height: 13, color: '#555' }} />
      </div>
      <p style={{ fontSize: 10, fontWeight: 500, color: '#111', marginBottom: 2 }}>Dictation</p>
      <p style={{ fontSize: 9, color: '#888', lineHeight: '1.3' }}>Push-to-record notes — faster than typing, cleaner than voice-to-text</p>
    </div>
    {/* Lumina Lens */}
    <div style={{
      background: isNonFhir ? '#f5f5f5' : '#f9f9f9',
      border: isNonFhir ? '0.5px solid #d1d5db' : '0.5px solid #e8e8e8',
      borderRadius: 8, padding: '9px 10px'
    }}>
      <div style={{
        width: 24, height: 24, borderRadius: 6,
        background: isNonFhir ? '#111' : '#f0f0f0',
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6
      }}>
        <LensMagnifier size={13} color={isNonFhir ? '#fff' : '#555'} />
      </div>
      <p style={{ fontSize: 10, fontWeight: 500, color: '#111', marginBottom: 2 }}>
        Lumina Lens
        {isNonFhir && <span style={{ fontSize: 8, fontWeight: 500, padding: '1px 5px', borderRadius: 4, background: '#111', color: '#fff', marginLeft: 4, verticalAlign: 'middle' }}>Primary</span>}
      </p>
      <p style={{ fontSize: 9, color: '#888', lineHeight: '1.3' }}>
        {isNonFhir
          ? 'Your main entry point — scan schedules, documents, labs, and referrals'
          : 'Scan referrals, labs, and discharge summaries instead of re-entering data'}
      </p>
    </div>
  </div>
);

/* ── Main Lightbox ── */
const LuminaOnboardingLightbox = ({ open, onClose, hasFhir = false }) => {
  const navigate = useNavigate();
  const [showLensDemo, setShowLensDemo] = useState(false);

  const [integration, setIntegration] = useState(() => {
    try { return localStorage.getItem(INTEGRATION_KEY) || (hasFhir ? 'fhir' : 'non-fhir'); } catch { return hasFhir ? 'fhir' : 'non-fhir'; }
  });

  useEffect(() => {
    try { localStorage.setItem(INTEGRATION_KEY, integration); } catch {}
  }, [integration]);

  if (!open) return null;

  const isFhir = integration === 'fhir';
  const stepCount = isFhir ? 3 : 4;

  const handleCta = () => {
    onClose();
    navigate('/lumina/clinical-focus');
  };

  const handleDontShow = (e) => {
    try {
      if (e.target.checked) localStorage.setItem(LIGHTBOX_DISMISSED_KEY, 'true');
      else localStorage.removeItem(LIGHTBOX_DISMISSED_KEY);
    } catch {}
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="lumina-workflow-lightbox">
        <div className="bg-white shadow-2xl w-full mx-4 flex flex-col" style={{ borderRadius: 14, maxWidth: 660, maxHeight: '90vh', animation: 'luminaLbFadeIn 0.2s ease' }}>

          {/* ── Header ── */}
          <div style={{ padding: '18px 20px 0', flexShrink: 0 }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={LUMINA_LOGO_BLACK} alt="Lumina" style={{ height: 14, width: 'auto', objectFit: 'contain' }} />
                <span style={{ fontSize: 10, color: '#111', letterSpacing: '.06em', fontWeight: 500 }}>&middot; Getting started</span>
              </div>
              <button onClick={onClose} className="flex items-center justify-center" style={{ width: 26, height: 26, borderRadius: '50%', background: '#f5f5f5', border: 'none', cursor: 'pointer' }} data-testid="close-lumina-lightbox">
                <X style={{ width: 13, height: 13, color: '#888' }} />
              </button>
            </div>
            <h2 style={{ fontSize: 17, fontWeight: 500, color: '#111', marginTop: 8 }}>Documentation, handled by AI</h2>
            <p style={{ fontSize: 11, color: '#888', marginTop: 3 }}>Here's how Lumina turns patient conversations into complete SOAP notes</p>

            {/* ── Integration toggle ── */}
            <div style={{ margin: '14px 0 0', padding: 3, background: '#f5f5f5', borderRadius: 7, display: 'flex', gap: 2 }}>
              <button
                onClick={() => setIntegration('fhir')}
                style={{ flex: 1, fontSize: 10, fontWeight: isFhir ? 500 : 400, color: isFhir ? '#111' : '#888', background: isFhir ? '#fff' : 'transparent', border: isFhir ? '0.5px solid #e8e8e8' : 'none', borderRadius: 5, padding: '5px 0', cursor: 'pointer', transition: 'all 0.15s' }}
                data-testid="lumina-tab-fhir"
              >FHIR connected</button>
              <button
                onClick={() => setIntegration('non-fhir')}
                style={{ flex: 1, fontSize: 10, fontWeight: !isFhir ? 500 : 400, color: !isFhir ? '#111' : '#888', background: !isFhir ? '#fff' : 'transparent', border: !isFhir ? '0.5px solid #e8e8e8' : 'none', borderRadius: 5, padding: '5px 0', cursor: 'pointer', transition: 'all 0.15s' }}
                data-testid="lumina-tab-non-fhir"
              >Non-FHIR &middot; using Lens</button>
            </div>

            {/* ── Conditional element below toggle ── */}
            {isFhir ? (
              <div style={{ background: '#eff6ff', border: '0.5px solid #bfdbfe', borderRadius: 7, padding: '7px 11px', marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }} data-testid="lumina-fhir-note">
                <div style={{ width: 16, height: 16, borderRadius: 3, background: '#bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 7, fontWeight: 700, color: '#1e40af' }}>F</span>
                </div>
                <p style={{ fontSize: 10, color: '#1e40af', lineHeight: '1.4' }}>Your EHR syncs patient data and schedules automatically via FHIR &mdash; no scanning needed to populate today's patients.</p>
              </div>
            ) : (
              <div style={{ background: '#f9f9f9', border: '0.5px solid #e8e8e8', borderRadius: 8, padding: '10px 13px', marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }} data-testid="lumina-lens-hero">
                <div style={{ width: 32, height: 32, borderRadius: 7, background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <LensMagnifier size={16} color="#fff" />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 11, fontWeight: 500, color: '#111' }}>Lens is how you get data into Lumina</p>
                  <p style={{ fontSize: 10, color: '#555', marginTop: 1 }}>Scan your appointment list, patient documents, or referral notes &mdash; instead of typing anything in manually.</p>
                </div>
                <button onClick={() => setShowLensDemo(true)} className="flex items-center gap-1.5" style={{ height: 28, padding: '0 12px', borderRadius: 6, background: '#111', color: '#fff', fontSize: 10, fontWeight: 500, border: 'none', cursor: 'pointer', flexShrink: 0 }} data-testid="lumina-try-lens-btn">
                  <LensMagnifier size={10} color="#fff" />Try Lens demo
                </button>
              </div>
            )}

            {/* ── Tools row ── */}
            <ToolsRow isNonFhir={!isFhir} />
          </div>

          {/* ── Body: Workflow steps ── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 16px' }}>
            <p style={{ fontSize: 9, color: '#bbb', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>Your visit workflow &mdash; {stepCount} steps</p>
            {isFhir
              ? <FHIRWorkflow onLensClick={() => setShowLensDemo(true)} />
              : <NonFHIRWorkflow onLensClick={() => setShowLensDemo(true)} />
            }
          </div>

          {/* ── Footer ── */}
          <div style={{ borderTop: '0.5px solid #f0f0f0', padding: '10px 20px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
              <input type="checkbox" style={{ accentColor: '#555' }} onChange={handleDontShow} data-testid="lumina-dont-show-checkbox" />
              <span style={{ fontSize: 10, color: '#aaa' }}>Don't show this again</span>
            </label>
            <button onClick={handleCta} className="flex items-center gap-1.5" style={{ height: 32, padding: '0 18px', borderRadius: 7, background: '#111', color: '#fff', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer' }} data-testid="lumina-onboarding-cta">
              <SparkleIcon size={11} color="#fff" />
              Start today's visits
              <ArrowRight style={{ width: 12, height: 12, opacity: 0.6 }} />
            </button>
          </div>
        </div>
        <style>{`@keyframes luminaLbFadeIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }`}</style>
      </div>

      {/* Lens demo as second layer modal */}
      <LensWorkflowDemo open={showLensDemo} onOpenChange={setShowLensDemo} />
    </>
  );
};

export default LuminaOnboardingLightbox;
export { LIGHTBOX_DISMISSED_KEY, INTEGRATION_KEY };
